import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Dropdown,
  Form,
  Input,
  Label,
  Modal,
  Segment,
} from 'semantic-ui-react';
import { API, isAdmin, showError, showSuccess } from '../../helpers';

const CATEGORIES = [
  { key: '', label: '全部' },
  { key: 'image', label: '图片' },
  { key: 'video', label: '视频' },
  { key: 'video_package', label: '视频包装' },
  { key: 'character', label: '角色' },
  { key: 'code', label: '代码' },
];

const STATUS_LABELS = { 1: '上架', 2: '下架' };

const emptyForm = {
  id: 0,
  name: '',
  category: 'image',
  description: '',
  icon_url: '',
  status: 1,
  sort_order: 0,
  models: '',
  channel_group: 'default',
  pricing: '',
  docs: '',
};

const Products = () => {
  const admin = isAdmin();
  const [category, setCategory] = useState('');
  const [keyword, setKeyword] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [docsOpen, setDocsOpen] = useState(null);
  const [availGroups, setAvailGroups] = useState([]);
  const [modelsByGroup, setModelsByGroup] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const url = admin
        ? `/api/product/?p=0${category ? `&category=${category}` : ''}`
        : `/api/products${category ? `?category=${category}` : ''}`;
      const res = await API.get(url);
      const { success, message, data } = res.data || {};
      if (success) {
        setItems(data || []);
      } else {
        showError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableOptions = async () => {
    if (!admin) return;
    try {
      const res = await API.get('/api/product/available_options');
      const { success, data } = res.data || {};
      if (success && data) {
        setAvailGroups(data.groups || []);
        setModelsByGroup(data.models_by_group || {});
      }
    } catch (e) {
      // 静默：表单仍可用，只是没下拉选项
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  useEffect(() => {
    loadAvailableOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!keyword) return items;
    const k = keyword.toLowerCase();
    return items.filter(
      (p) =>
        p.name.toLowerCase().includes(k) ||
        (p.description || '').toLowerCase().includes(k) ||
        (p.models || '').toLowerCase().includes(k)
    );
  }, [items, keyword]);

  const openAdd = () => {
    setForm({ ...emptyForm });
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setForm({ ...emptyForm, ...p });
    setModalOpen(true);
  };

  const submit = async (force = false) => {
    try {
      const payload = { ...form, sort_order: Number(form.sort_order) || 0, status: Number(form.status) };
      const url = (path) => (force ? `${path}${path.includes('?') ? '&' : '?'}force=1` : path);
      const res = form.id
        ? await API.put(url('/api/product/'), payload)
        : await API.post(url('/api/product/'), payload);
      const { success, message, code, data } = res.data || {};
      if (success) {
        showSuccess(form.id ? '已更新' : '已创建');
        setModalOpen(false);
        load();
        return;
      }
      if (code === 'channel_missing' && !force) {
        const missing = (data && data.missing) || [];
        const ok = window.confirm(
          `以下模型在分组「${data?.group || form.channel_group}」下当前没有可用渠道：\n\n${missing.join(', ')}\n\n仍要保存吗？（可以稍后再去配置渠道）`
        );
        if (ok) return submit(true);
        return;
      }
      showError(message);
    } catch (e) {
      showError(e.message);
    }
  };

  const toggleStatus = async (p) => {
    const next = p.status === 1 ? 2 : 1;
    const res = await API.put('/api/product/?status_only=1', { id: p.id, status: next });
    const { success, message } = res.data || {};
    if (success) {
      showSuccess(next === 1 ? '已上架' : '已下架');
      load();
    } else {
      showError(message);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`确定删除产品「${p.name}」？`)) return;
    const res = await API.delete(`/api/product/${p.id}`);
    const { success, message } = res.data || {};
    if (success) {
      showSuccess('已删除');
      load();
    } else {
      showError(message);
    }
  };

  const groupOptions = useMemo(() => {
    const set = new Set(availGroups);
    if (form.channel_group) set.add(form.channel_group);
    return Array.from(set).map((g) => ({ key: g, value: g, text: g }));
  }, [availGroups, form.channel_group]);

  const modelOptions = useMemo(() => {
    const groupKey = form.channel_group || 'default';
    const allowed = modelsByGroup[groupKey] || [];
    const selected = (form.models || '').split(',').map((s) => s.trim()).filter(Boolean);
    const optionSet = [...new Set([...allowed, ...selected])];
    return optionSet.map((m) => ({
      key: m,
      value: m,
      text: allowed.includes(m) ? m : `${m} (无可用渠道)`,
    }));
  }, [modelsByGroup, form.channel_group, form.models]);

  const selectedModels = (form.models || '').split(',').map((s) => s.trim()).filter(Boolean);
  const allowedForGroup = modelsByGroup[form.channel_group || 'default'] || [];
  const orphanModels = selectedModels.filter((m) => !allowedForGroup.includes(m));

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>模型广场</Card.Header>
          <Card.Description style={{ marginTop: '0.5em', color: '#6b7280' }}>
            按分类浏览全部模型/产品。{admin ? '管理员可在此上下架与编辑。' : ''}
          </Card.Description>

          <Segment basic style={{ padding: '1em 0 0 0' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', alignItems: 'center' }}>
              <Button.Group size='small'>
                {CATEGORIES.map((c) => (
                  <Button
                    key={c.key || 'all'}
                    primary={category === c.key}
                    basic={category !== c.key}
                    onClick={() => setCategory(c.key)}
                  >
                    {c.label}
                  </Button>
                ))}
              </Button.Group>
              <Input
                size='small'
                icon='search'
                placeholder='搜索名称 / 描述 / 模型...'
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                style={{ marginLeft: 'auto', minWidth: '240px' }}
              />
              {admin && (
                <Button primary size='small' icon='plus' content='添加产品' onClick={openAdd} />
              )}
            </div>
          </Segment>

          {loading ? (
            <Segment basic textAlign='center' style={{ color: '#888' }}>加载中...</Segment>
          ) : filtered.length === 0 ? (
            <Segment placeholder textAlign='center' style={{ color: '#888' }}>
              暂无产品
            </Segment>
          ) : (
            <Card.Group itemsPerRow={3} stackable doubling style={{ marginTop: '1em' }}>
              {filtered.map((p) => (
                <Card key={p.id}>
                  <Card.Content>
                    <Card.Header style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name}
                      </span>
                      <Label size='mini' color={p.status === 1 ? 'green' : 'grey'}>
                        {STATUS_LABELS[p.status] || '-'}
                      </Label>
                    </Card.Header>
                    <Card.Meta style={{ marginTop: '0.4em' }}>
                      分类：{CATEGORIES.find((c) => c.key === p.category)?.label || p.category}
                      {' · '}
                      分组：{p.channel_group || 'default'}
                    </Card.Meta>
                    {p.description && (
                      <Card.Description style={{ marginTop: '0.6em', color: '#444' }}>
                        {p.description}
                      </Card.Description>
                    )}
                    {p.models && (
                      <div style={{ marginTop: '0.7em', display: 'flex', flexWrap: 'wrap', gap: '0.3em' }}>
                        {p.models.split(',').map((m) => m.trim()).filter(Boolean).slice(0, 6).map((m) => (
                          <Label key={m} size='mini' basic>
                            {m}
                          </Label>
                        ))}
                      </div>
                    )}
                    {p.pricing && (
                      <div style={{ marginTop: '0.7em', fontSize: '0.85em', color: '#555' }}>
                        <strong>定价：</strong>
                        <code style={{ wordBreak: 'break-all' }}>{p.pricing}</code>
                      </div>
                    )}
                  </Card.Content>
                  {(p.docs || admin) && (
                    <Card.Content extra>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3em' }}>
                        {p.docs && (
                          <Button size='mini' icon='file alternate' content='文档' onClick={() => setDocsOpen(p)} />
                        )}
                        {admin && (
                          <>
                            <Button size='mini' icon='edit' content='编辑' onClick={() => openEdit(p)} />
                            <Button
                              size='mini'
                              color={p.status === 1 ? 'grey' : 'green'}
                              content={p.status === 1 ? '下架' : '上架'}
                              onClick={() => toggleStatus(p)}
                            />
                            <Button size='mini' negative icon='trash' content='删除' onClick={() => remove(p)} />
                          </>
                        )}
                      </div>
                    </Card.Content>
                  )}
                </Card>
              ))}
            </Card.Group>
          )}
        </Card.Content>
      </Card>

      {/* 添加 / 编辑 弹窗 */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size='small'>
        <Modal.Header>{form.id ? '编辑产品' : '添加产品'}</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field required>
              <label>名称</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Form.Field>
            <Form.Group widths='equal'>
              <Form.Field required>
                <label>分类</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className='ui dropdown'
                >
                  {CATEGORIES.filter((c) => c.key).map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </Form.Field>
              <Form.Field required>
                <label>状态</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: Number(e.target.value) })}
                  className='ui dropdown'
                >
                  <option value={1}>上架</option>
                  <option value={2}>下架</option>
                </select>
              </Form.Field>
              <Form.Field>
                <label>排序</label>
                <input
                  type='number'
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                />
              </Form.Field>
            </Form.Group>
            <Form.Field>
              <label>渠道分组</label>
              <Dropdown
                selection
                search
                allowAdditions
                options={groupOptions}
                value={form.channel_group || 'default'}
                onAddItem={(_, { value }) =>
                  setAvailGroups((prev) => (prev.includes(value) ? prev : [...prev, value]))
                }
                onChange={(_, { value }) => {
                  const allowed = new Set(modelsByGroup[value] || []);
                  const cur = (form.models || '').split(',').map((s) => s.trim()).filter(Boolean);
                  setForm({ ...form, channel_group: value, models: cur.filter((m) => allowed.has(m)).join(',') });
                }}
                placeholder='选择已有渠道分组'
              />
              <div style={{ marginTop: '0.3em', fontSize: '0.8em', color: '#888' }}>
                来自已启用渠道的分组。找不到？检查渠道是否已配置并启用。
              </div>
            </Form.Field>
            <Form.Field>
              <label>模型列表</label>
              <Dropdown
                selection
                multiple
                search
                allowAdditions
                options={modelOptions}
                value={selectedModels}
                onChange={(_, { value }) => setForm({ ...form, models: (value || []).join(',') })}
                placeholder={
                  allowedForGroup.length === 0
                    ? `分组「${form.channel_group || 'default'}」下还没有任何启用的模型，请先配置渠道`
                    : '从已启用渠道的模型中选择，或手动添加'
                }
              />
              {orphanModels.length > 0 && (
                <div style={{ marginTop: '0.3em', fontSize: '0.8em', color: '#d97706' }}>
                  ⚠ 以下模型在当前分组下没有可用渠道：{orphanModels.join(', ')}
                </div>
              )}
            </Form.Field>
            <Form.Field>
              <label>描述</label>
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Form.Field>
            <Form.Field>
              <label>定价（JSON 字符串，前端自行展示）</label>
              <textarea
                rows={2}
                value={form.pricing}
                onChange={(e) => setForm({ ...form, pricing: e.target.value })}
                placeholder='{"unit":"per_call","price":0.5}'
              />
            </Form.Field>
            <Form.Field>
              <label>文档（Markdown）</label>
              <textarea
                rows={5}
                value={form.docs}
                onChange={(e) => setForm({ ...form, docs: e.target.value })}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setModalOpen(false)}>取消</Button>
          <Button primary onClick={() => submit(false)}>{form.id ? '保存' : '创建'}</Button>
        </Modal.Actions>
      </Modal>

      {/* 文档弹窗 */}
      <Modal open={!!docsOpen} onClose={() => setDocsOpen(null)} size='small'>
        <Modal.Header>{docsOpen?.name} · 文档</Modal.Header>
        <Modal.Content>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.9em', color: '#333' }}>
            {docsOpen?.docs}
          </pre>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDocsOpen(null)}>关闭</Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default Products;
